import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Printer, Pencil, Trash2 } from "lucide-react";
import { api, formatApiError, brl } from "@/lib/api";

const MONTHS = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const YEARS = [2024, 2025, 2026, 2027];

const now = new Date();

const fmtDay = (d) => (d ? String(Number(d.slice(8, 10))) : "");
const fmtShort = (d) => {
  if (!d) return "";
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y.slice(2)}`;
};

const addMonths = (dateStr, m) => {
  const [y, mo, d] = dateStr.split("-").map(Number);
  const t = new Date(Date.UTC(y, mo - 1 + m, 1));
  const lastDay = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + 1, 0)).getUTCDate();
  t.setUTCDate(Math.min(d, lastDay));
  return t.toISOString().slice(0, 10);
};

const seqDoc = (doc, offset) => {
  if (!doc || !offset || !/\d/.test(doc)) return doc || "";
  return doc.replace(/\d+/, (n) => String(Number(n) + offset));
};

export default function Pagar() {
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [onlyPending, setOnlyPending] = useState(false);
  const [bills, setBills] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dialog, setDialog] = useState(null); // {bill?}
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const fetchAll = () => {
    api.get("/bills", { params: { month, year } }).then((r) => setBills(r.data)).catch(() => {});
    api.get("/finance/summary", { params: { month, year } }).then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(() => { fetchAll(); }, [month, year]);

  const shown = onlyPending ? bills.filter((b) => !b.dt_pgto) : bills;
  const totalDespesas = bills.reduce((s, b) => s + (Number(b.valor) || 0), 0);

  const openDialog = (bill) => {
    setDialog({});
    setForm(
      bill
        ? { venc_date: bill.venc_date, doc: bill.doc || "", obrigacao: bill.obrigacao, fornecedor: bill.fornecedor || "", valor: String(bill.valor), dt_pgto: bill.dt_pgto || "", repeat: "1" }
        : { venc_date: `${year}-${String(month).padStart(2, "0")}-01`, doc: "", obrigacao: "", fornecedor: "", valor: "", dt_pgto: "", repeat: "1" }
    );
  };

  const save = async () => {
    if (!form.obrigacao.trim() || !form.venc_date) {
      toast.error("Preencha a obrigação e o vencimento");
      return;
    }
    setBusy(true);
    const base = { doc: form.doc.trim(), obrigacao: form.obrigacao.trim(), fornecedor: form.fornecedor.trim(), valor: Number(form.valor) || 0 };
    const repeat = Math.max(1, Math.min(36, Number(form.repeat) || 1));
    try {
      for (let i = 0; i < repeat; i++) {
        const payload = {
          venc_date: addMonths(form.venc_date, i),
          doc: seqDoc(form.doc.trim(), i),
          obrigacao: base.obrigacao,
          fornecedor: base.fornecedor,
          valor: base.valor,
          dt_pgto: i === 0 ? form.dt_pgto || "" : "",
        };
        if (dialog.bill) await api.put(`/bills/${dialog.bill.id}`, payload);
        else await api.post("/bills", payload);
      }
      toast.success(repeat > 1 ? `${repeat} contas cadastradas` : "Conta salva");
      setDialog(null);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Excluir a conta "${b.obrigacao}"?`)) return;
    try {
      await api.delete(`/bills/${b.id}`);
      toast.success("Conta excluída");
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const field = "w-full rounded-lg border border-stone-300 px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#C8A279] focus:ring-2 focus:ring-[#C8A279]/20 transition-all";

  return (
    <div data-testid="pagar-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6 no-print">
        <h1 className="font-display text-3xl sm:text-4xl font-light">Contas a Pagar</h1>
        <div className="flex flex-wrap items-center gap-2">
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="rounded-full border border-stone-300 bg-white px-4 py-2.5 text-sm focus:outline-none" data-testid="pagar-month-filter">
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="rounded-full border border-stone-300 bg-white px-4 py-2.5 text-sm focus:outline-none" data-testid="pagar-year-filter">
            {YEARS.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            onClick={() => setOnlyPending(!onlyPending)}
            className={`px-5 py-2.5 rounded-full text-sm border transition-colors ${onlyPending ? "bg-amber-500 text-white border-amber-500" : "border-stone-300 text-stone-600 hover:border-stone-900"}`}
            data-testid="pagar-pending-toggle"
          >
            {onlyPending ? "Mostrando a pagar" : "Mostrar só a pagar"}
          </button>
          <button onClick={() => openDialog(null)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#121212] text-white text-sm font-medium hover:bg-stone-700 transition-colors" data-testid="pagar-new-button">
            <Plus size={15} /> Inserir
          </button>
          <button onClick={() => window.print()} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-stone-300 text-sm text-stone-600 hover:border-stone-900 transition-colors" data-testid="pagar-print-button">
            <Printer size={15} /> Imprimir / PDF
          </button>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl p-5 sm:p-8 shadow-[0_2px_12px_rgba(0,0,0,0.03)] print-area">
        {/* Cabeçalho do relatório */}
        <div className="text-center mb-6">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">MARCIO PISSAIA Cabeleireiro</h2>
          <p className="text-lg mt-1">CONTAS A PAGAR / PAGAS</p>
          <p className="text-sm text-stone-500 mt-1">{MONTHS[month - 1]} {year}</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="pagar-table">
            <thead>
              <tr className="border-b-2 border-stone-300 text-left text-xs font-semibold tracking-[0.08em] uppercase text-stone-600">
                <th className="py-2.5 pr-3 w-16">Venc</th>
                <th className="py-2.5 pr-3 w-24">Ref</th>
                <th className="py-2.5 pr-3">Conta</th>
                <th className="py-2.5 pr-3">Fornecedor</th>
                <th className="py-2.5 pr-3 text-right">Valor</th>
                <th className="py-2.5 pr-3 w-28">Pgto</th>
                <th className="py-2.5 no-print w-20" />
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-stone-400">Nenhuma conta cadastrada neste mês.</td>
                </tr>
              )}
              {shown.map((b) => (
                <tr key={b.id} className={`border-b border-stone-100 hover:bg-stone-50 transition-colors ${b.dt_pgto ? "text-stone-800" : "text-red-600 bg-red-50/40"}`} data-testid={`pagar-row-${b.id}`}>
                  <td className="py-2.5 pr-3 tabular-nums">{fmtDay(b.venc_date)}</td>
                  <td className="py-2.5 pr-3" data-testid={`pagar-doc-${b.id}`}>{b.doc}</td>
                  <td className="py-2.5 pr-3 font-medium">{b.obrigacao}</td>
                  <td className="py-2.5 pr-3 text-right">{b.fornecedor}</td>
                  <td className="py-2.5 pr-3 text-right tabular-nums" data-testid={`pagar-valor-${b.id}`}>{(Number(b.valor) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</td>
                  <td className="py-2.5 pr-3 tabular-nums">{fmtShort(b.dt_pgto)}</td>
                  <td className="py-2.5 no-print">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => openDialog(b)} className="p-2 rounded-full text-stone-400 hover:text-stone-800 hover:bg-stone-100 transition-colors" data-testid={`pagar-edit-${b.id}`} aria-label="Alterar">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => remove(b)} className="p-2 rounded-full text-stone-300 hover:text-red-500 hover:bg-red-50 transition-colors" data-testid={`pagar-delete-${b.id}`} aria-label="Excluir">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-stone-300">
                <td colSpan={4} className="py-2.5 pr-3 text-right font-medium text-stone-600">Despesas</td>
                <td className="py-2.5 text-right font-semibold tabular-nums" data-testid="pagar-despesas">{totalDespesas.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</td>
                <td colSpan={2} className="no-print" />
              </tr>
              {summary && (
                <>
                  <tr>
                    <td colSpan={4} className="py-2.5 pr-3 text-right font-medium text-stone-600">Receitas</td>
                    <td className="py-2.5 text-right font-semibold tabular-nums border-b border-dashed border-stone-400" data-testid="pagar-receitas">{summary.revenue.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</td>
                    <td colSpan={2} className="no-print" />
                  </tr>
                  <tr>
                    <td colSpan={4} className="py-2.5 pr-3 text-right font-medium text-stone-600">Lucro</td>
                    <td className={`py-2.5 text-right font-semibold tabular-nums ${summary.net < 0 ? "text-red-600" : "text-green-700"}`} data-testid="pagar-lucro">{summary.net.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</td>
                    <td colSpan={2} className="no-print" />
                  </tr>
                </>
              )}
            </tfoot>
          </table>
        </div>
      </div>

      {dialog && (
        <div className="fixed inset-0 z-[140] flex items-center justify-center p-4 bg-black/50" data-testid="pagar-dialog">
          <div className="bg-[#FAF9F5] rounded-2xl w-full max-w-lg shadow-2xl max-h-[92vh] overflow-y-auto thin-scroll">
            <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between">
              <p className="font-display text-xl">{dialog.bill ? "Alterar conta" : "Inserir conta"}</p>
              <button onClick={() => setDialog(null)} className="p-2 rounded-full hover:bg-stone-200" aria-label="Fechar" data-testid="pagar-dialog-close">×</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500">Vencimento</label>
                  <input type="date" value={form.venc_date} onChange={(e) => setForm({ ...form, venc_date: e.target.value })} className={`mt-1.5 ${field}`} data-testid="pagar-vencimento-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500">Doc</label>
                  <input value={form.doc} onChange={(e) => setForm({ ...form, doc: e.target.value })} className={`mt-1.5 ${field}`} data-testid="pagar-doc-input" placeholder="09/2026" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500">Obrigação</label>
                <input value={form.obrigacao} onChange={(e) => setForm({ ...form, obrigacao: e.target.value })} className={`mt-1.5 ${field}`} data-testid="pagar-obrigacao-input" placeholder="Ex.: Energia Elétrica, Aluguel…" />
              </div>
              <div>
                <label className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500">Fornecedor</label>
                <input value={form.fornecedor} onChange={(e) => setForm({ ...form, fornecedor: e.target.value })} className={`mt-1.5 ${field}`} data-testid="pagar-fornecedor-input" placeholder="Ex.: Copel, Sanepar…" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500">Valor (R$)</label>
                  <input type="number" step="0.01" min="0" value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} className={`mt-1.5 ${field}`} data-testid="pagar-valor-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500">Dt. Pgto.</label>
                  <input type="date" value={form.dt_pgto} onChange={(e) => setForm({ ...form, dt_pgto: e.target.value })} className={`mt-1.5 ${field}`} data-testid="pagar-dtpgto-input" />
                </div>
              </div>
              {!dialog.bill && (
                <div>
                  <label className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500">Repetir por (meses)</label>
                  <input
                    type="number"
                    min="1"
                    max="36"
                    value={form.repeat}
                    onChange={(e) => setForm({ ...form, repeat: e.target.value })}
                    className={`mt-1.5 ${field}`}
                    data-testid="pagar-repeat-input"
                  />
                  <p className="text-xs text-stone-400 mt-1.5">
                    Cria a mesma conta nos próximos meses, repetindo o valor e sequenciando o Doc (ex.: 09/2026 → 10/2026, 11/2026…)
                  </p>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-stone-200 flex gap-3 justify-end">
              <button onClick={() => setDialog(null)} className="px-5 py-2.5 rounded-full border border-stone-300 text-sm text-stone-600 hover:border-stone-900 transition-colors" data-testid="pagar-dialog-cancel">Cancelar</button>
              <button onClick={save} disabled={busy} className="px-7 py-2.5 rounded-full bg-[#121212] text-white text-sm font-medium hover:bg-stone-700 transition-colors disabled:opacity-50" data-testid="pagar-dialog-save">
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
